import { formatValue } from "./helpers";

interface BarLabelProps {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    value?: number;
}

export const BarValueLabel = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    value = 0,
}: BarLabelProps) => {
    return (
        <text
            x={x + width + 6}
            y={y + height / 2}
            dy="0.35em"
            fill="#475569"
            fontSize={10}
            fontWeight={600}
            textAnchor="start"
        >
            {Number(value).toLocaleString()}
        </text>
    );
};

interface BarPercentageLabelProps extends BarLabelProps {
    total?: number;
}

export const BarPercentageLabel = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    value = 0,
    total = 0,
}: BarPercentageLabelProps) => {
    if (total <= 0) {
        return null;
    }

    const percentage = (Number(value) / total) * 100;
    const label = `${percentage.toFixed(0)}%`;

    /*
     * Approximate the amount of horizontal room needed for the label.
     * Increase this threshold if the text feels crowded.
     */
    const minimumWidth = label.length * 7 + 12;

    if (width < minimumWidth) {
        return null;
    }

    return (
        <text
            x={x + 7}
            y={y + height / 2}
            dy="0.35em"
            fill="#ffffff"
            fontSize={10}
            fontWeight={600}
            textAnchor="start"
            pointerEvents="none"
        >
            {label}
        </text>
    );
};

interface BarShareLabelProps extends BarLabelProps {
    unit?: string;
}

export const BarShareLabel = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    value = 0,
    unit = "percentage",
}: BarShareLabelProps) => {
    const label = formatValue(Number(value), unit);

    /*
     * Only render the share inside the bar when
     * there is sufficient horizontal space.
     */
    const minimumWidth = label.length * 7 + 12;

    if (width < minimumWidth) {
        return null;
    }

    return (
        <text
            x={x + 7}
            y={y + height / 2}
            dy="0.35em"
            fill="#ffffff"
            fontSize={10}
            fontWeight={600}
            textAnchor="start"
            pointerEvents="none"
        >
            {label}
        </text>
    );
};

export const CustomTooltipBar = ({
    active,
    payload,
    label,
    total,
}: any) => {
    if (active && payload && payload.length) {
        const value = Number(payload[0].value);

        const percentage =
            total > 0
                ? ((value / total) * 100).toFixed(1)
                : "0.0";

        return (
            <div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs min-w-[120px]">
                <p className="font-semibold mb-1">{label}</p>

                <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Count</span>
                    <span className="font-medium">
                        {value.toLocaleString()}
                    </span>
                </div>

                <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Share</span>
                    <span className="font-medium">
                        {percentage}%
                    </span>
                </div>
            </div>
        );
    }

    return null;
};

export const CustomTick = ({
    x,
    y,
    payload,
    maxLineLength = 20,
}: any) => {
    const raw =
        typeof payload.value === "string"
            ? payload.value
            : String(payload.value);

    const label = raw
        .replace(/\u00A0/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const words = label.split(" ");
    const lines: string[] = [];

    let currentLine = "";

    words.forEach((word: any) => {
        const nextLine = currentLine
            ? `${currentLine} ${word}`
            : word;

        if (nextLine.length <= maxLineLength) {
            currentLine = nextLine;
        } else {
            if (currentLine) {
                lines.push(currentLine);
            }

            currentLine = word;
        }
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    if (lines.length === 1) {
        return (
            <text
                x={x}
                y={y}
                dy={2}
                textAnchor="end"
                fill="#666"
                style={{
                    whiteSpace: "nowrap",
                    fontSize: 10,
                }}
            >
                {lines[0]}
            </text>
        );
    }

    return (
        <text
            x={x}
            y={y}
            textAnchor="end"
            fill="#666"
            style={{ fontSize: 10 }}
        >
            {lines.map((line, index) => (
                <tspan
                    key={`${line}-${index}`}
                    x={x}
                    dy={index === 0 ? -1 : 9}
                >
                    {line}
                </tspan>
            ))}
        </text>
    );
};

